import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoSearchOutline } from "react-icons/io5";
import ConversationList from "./ConversationList";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks";
import { FiPlusSquare } from "react-icons/fi";
import { updateCreateGroupData } from "../../../store/Slices/CreateGroupSlice";
import { updateViewState } from "../../../store/Slices/ViewManagerSlice";
import ArchiveConversationList from "./ArchiveConversationList";
import TextTranslate from "../../../utils/TextTranslate";
import { useTranslateText } from "../../../hooks/useTranslateText";
import SearchChatList from "./SearchChatList";
import { RxCross2 } from "react-icons/rx";

export default function Chat() {
  const ViewManager = useAppSelector((state) => state.ViewManager);
  const archiveList = useAppSelector((state) => state.archiveList);
  const ContactList = useAppSelector((state) => state.ContactList);

  const dispatch = useAppDispatch();
  const [searchUser, setsearchUser] = useState("");
  const navigate = useNavigate();
  const translate = useTranslateText();

  const archiveCount = archiveList.Chats?.length ?? 0;

  useEffect(() => {
    if (archiveCount === 0 && ViewManager.showArchiveList) {
      dispatch(updateViewState({ showArchiveList: false }));
    }
  }, [archiveCount, ViewManager.showArchiveList, dispatch]);

  const archivePreview = useMemo(() => {
    return (archiveList.Chats ?? [])
      .map((chat) => {
        if (chat.Records[0]?.chat_type === "group") {
          return chat.Records[0].group_name?.trim() || "";
        }
        const peerId = chat.PeerUserData?.user_id;
        const saved = ContactList?.data?.find((c) => c.user_id === peerId)?.name;
        return (
          saved?.trim() ||
          chat.PeerUserData?.full_name?.trim() ||
          chat.PeerUserData?.user_name?.trim() ||
          ""
        );
      })
      .filter(Boolean)
      .join(", ");
  }, [archiveList.Chats, ContactList?.data]);

  return (
    <div className="relative z-30 flex h-[100dvh] w-full flex-col overflow-hidden bg-secondary pb-[calc(3.25rem+env(safe-area-inset-bottom))] lg:pb-0">
      <div className="sticky top-0 z-20 shrink-0 border-b border-black/[0.06] bg-secondary/85 backdrop-blur-xl dark:border-white/[0.08]">
        <div className="px-4 pb-3 pt-4 lg:pt-6">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h4 className="text-[28px] font-bold leading-tight tracking-tight text-darkText">
              <TextTranslate text="Chat" />
            </h4>
            <button
              type="button"
              onClick={() => {
                dispatch(
                  updateCreateGroupData({
                    createNewGroup: true,
                    user_id: [],
                    existing_member_user_id: [],
                    group_name: "",
                    group_description: "",
                  }),
                );
                navigate("/add-member-to-group");
              }}
              className="flex shrink-0 items-center gap-1.5 rounded-full bg-themeColor px-3 py-1.5 text-[13px] font-semibold text-white transition active:scale-95"
            >
              <FiPlusSquare className="text-base" />
              <span>
                <TextTranslate text="New Group" />
              </span>
            </button>
          </div>

          <div className="relative">
            <IoSearchOutline className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-lg text-lightText" />
            <input
              value={searchUser}
              onChange={(e) => setsearchUser(e.target.value)}
              className="w-full rounded-[10px] border-0 bg-black/[0.06] py-2.5 pl-10 pr-10 text-[15px] text-darkText outline-none placeholder:text-lightText dark:bg-white/[0.1]"
              type="search"
              enterKeyHint="search"
              placeholder={translate("Search User")}
            />
            {searchUser.length > 0 && (
              <button
                type="button"
                aria-label="Clear"
                onClick={() => setsearchUser("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-lightText"
              >
                <RxCross2 className="text-lg" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-hidden">
        {searchUser.length > 0 ? (
          <SearchChatList searchUser={searchUser} />
        ) : (
          <div className="chat-list-scroll flex h-full min-h-0 w-full flex-col overflow-y-auto overflow-x-hidden">
            {archiveCount > 0 && (
              <button
                type="button"
                onClick={() => {
                  dispatch(
                    updateViewState({
                      showArchiveList: !ViewManager.showArchiveList,
                    }),
                  );
                }}
                className={`flex w-full items-center gap-3 px-4 py-3 text-left transition active:bg-black/[0.03] hover:bg-selectedChat dark:active:bg-white/[0.04] ${
                  ViewManager.showArchiveList ? "bg-selectedChat" : ""
                }`}
              >
                <div className="grid h-14 w-14 shrink-0 place-content-center rounded-full bg-themeColor">
                  <div
                    className="h-6 w-6"
                    style={{
                      backgroundColor: "#fff",
                      maskImage: "url('/Home/archive_chat.png')",
                      WebkitMaskImage: "url('/Home/archive_chat.png')",
                      maskRepeat: "no-repeat",
                      WebkitMaskRepeat: "no-repeat",
                      maskSize: "100% 100%",
                      WebkitMaskSize: "100% 100%",
                      maskPosition: "center",
                      WebkitMaskPosition: "center",
                    }}
                    aria-hidden
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[16px] font-semibold text-darkText">
                    <TextTranslate text="Archive" />
                  </div>
                  <div className="mt-0.5 truncate text-[13px] text-lightText">
                    {archivePreview}
                  </div>
                </div>
                <span className="grid h-5 min-w-5 shrink-0 place-content-center rounded-full bg-themeColor px-1.5 text-[11px] font-semibold text-white">
                  {archiveCount}
                </span>
              </button>
            )}

            {ViewManager.showArchiveList ? (
              <ArchiveConversationList searchUser={searchUser} />
            ) : (
              <ConversationList searchUser={searchUser} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
